import { ArenaGlobalChatMessageSchema } from "./arena-chat.dto";
import { z } from "zod";

export const ARENA_FEED_LIMIT = 50;
export const ARENA_FEED_REMATCH_WINDOW_MS = 30 * 60 * 1000;
export const ARENA_FEED_JOIN_HOLD_MS = 10 * 1000;
export const ARENA_FEED_JOIN_DEDUPE_MS = 10 * 60 * 1000;
export const ARENA_FEED_JOIN_CTA_TTL_MS = 10 * 60 * 1000;
export const ARENA_FEED_STALE_DUEL_GRACE_MS = 15 * 60 * 1000;

export const ArenaFeedEventTypeSchema = z.enum(["QUEUE_JOINED", "DUEL_STARTED", "DUEL_ENDED", "PAYOUT"]);
export type ArenaFeedEventType = z.infer<typeof ArenaFeedEventTypeSchema>;
export const ArenaFeedItemTypeSchema = z.enum([...ArenaFeedEventTypeSchema.options, "CHAT"]);
export type ArenaFeedItemType = z.infer<typeof ArenaFeedItemTypeSchema>;
export const ArenaFeedDuelOutcomeSchema = z.enum(["WON", "WON_FREE", "NO_WINNER"]);
export type ArenaFeedDuelOutcome = z.infer<typeof ArenaFeedDuelOutcomeSchema>;

export const ArenaFeedPlayerSchema = z.object({
  hubUserId: z.number().int().positive(),
  nickname: z.string().nullable(), // resolved on read; client falls back to `Player #<id>`
});
export type ArenaFeedPlayer = z.infer<typeof ArenaFeedPlayerSchema>;

export const ArenaFeedRungSchema = z.object({
  tier: z.enum(["FREE", "PAID", "SPIN_AND_GO"]), // ONBOARDING never reaches the feed
  buyInUsdt: z.string(), // decimal string, "0" for FREE
});
export type ArenaFeedRung = z.infer<typeof ArenaFeedRungSchema>;

const base = { id: z.string(), createdAt: z.string() /* ISO */ };

export const ArenaFeedQueueJoinedSchema = z.object({
  ...base,
  type: z.literal("QUEUE_JOINED"),
  rung: ArenaFeedRungSchema,
});
export type ArenaFeedQueueJoined = z.infer<typeof ArenaFeedQueueJoinedSchema>;
export const ArenaFeedDuelStartedSchema = z.object({
  ...base,
  type: z.literal("DUEL_STARTED"),
  campaignName: z.string(),
  rung: ArenaFeedRungSchema,
  players: z.array(ArenaFeedPlayerSchema).min(2), // two seats, or a Spin & Go table's three
  isRematch: z.boolean(),
  endsAt: z.string(),
});
export type ArenaFeedDuelStarted = z.infer<typeof ArenaFeedDuelStartedSchema>;
export const ArenaFeedDuelEndedSchema = z.object({
  ...base,
  type: z.literal("DUEL_ENDED"),
  campaignName: z.string(),
  rung: ArenaFeedRungSchema,
  players: z.array(ArenaFeedPlayerSchema).min(2),
  isRematch: z.boolean(),
  outcome: ArenaFeedDuelOutcomeSchema,
  winnerHubUserId: z.number().int().positive().nullable(), // null iff NO_WINNER
  prizeUsdt: z.string().nullable(), // what the winner is credited, set iff WON
  potUsdt: z.string().nullable().default(null), // the final prize pool; null when unrecorded (older events)
});
export type ArenaFeedDuelEnded = z.infer<typeof ArenaFeedDuelEndedSchema>;
export const ArenaFeedPayoutSchema = z.object({
  ...base,
  type: z.literal("PAYOUT"),
  player: ArenaFeedPlayerSchema,
  amountUsdt: z.string(),
  claimTxHash: z.string(),
  explorerTxUrl: z.string(),
  chainName: z.string(),
});
export type ArenaFeedPayout = z.infer<typeof ArenaFeedPayoutSchema>;
export const ArenaFeedEventSchema = z.discriminatedUnion("type", [
  ArenaFeedQueueJoinedSchema,
  ArenaFeedDuelStartedSchema,
  ArenaFeedDuelEndedSchema,
  ArenaFeedPayoutSchema,
]);
export type ArenaFeedEvent = z.infer<typeof ArenaFeedEventSchema>;
export const ArenaFeedChatItemSchema = z.object({
  ...base,
  type: z.literal("CHAT"),
  message: ArenaGlobalChatMessageSchema,
});
export type ArenaFeedChatItem = z.infer<typeof ArenaFeedChatItemSchema>;
export const ArenaFeedItemSchema = z.discriminatedUnion("type", [
  ...ArenaFeedEventSchema.options,
  ArenaFeedChatItemSchema,
]);
export type ArenaFeedItem = z.infer<typeof ArenaFeedItemSchema>;

// GET /v1/cede-arena/feed?types=A,B&limit=50  (types omitted = all five)
export const ListArenaFeedQuerySchema = z.object({
  types: z.string().max(64).optional(), // comma list of ArenaFeedItemType; the full list is 48 chars
  limit: z.coerce.number().int().min(1).max(ARENA_FEED_LIMIT).default(ARENA_FEED_LIMIT),
});
export type ListArenaFeedQuery = z.infer<typeof ListArenaFeedQuerySchema>;
export const ListArenaFeedResponseSchema = z.object({ items: ArenaFeedItemSchema.array() }); // ascending by createdAt: newest LAST
export type ListArenaFeedResponse = z.infer<typeof ListArenaFeedResponseSchema>;

export const ARENA_FEED_FILTERS = {
  all: ["QUEUE_JOINED", "DUEL_STARTED", "DUEL_ENDED", "PAYOUT", "CHAT"],
  chat: ["CHAT"],
  duels: ["QUEUE_JOINED", "DUEL_STARTED", "DUEL_ENDED"],
  payouts: ["PAYOUT"],
} as const satisfies Record<string, ReadonlyArray<ArenaFeedItemType>>;
export type ArenaFeedFilter = keyof typeof ARENA_FEED_FILTERS;
